'use client';

import {
	DataGridPro,
	GridPaginationModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	getGridDateOperators,
	getGridNumericOperators,
} from '@mui/x-data-grid-pro';
import { useQuery } from '@parkchirp/api-client';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { GET_MONTHLY_PARKER_VEHICLES_CSV, LIST_VEHICLES_COUNT, LIST_VEHICLES_V2 } from '../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../types';
import { datagridFilterPanelAttributes, dayjs } from '../../utils';
import { DatagridLinearProgress } from '../loading';
import { CsvExportButtonV2 } from '../reports';
import { ParkerDetailModal } from './parkerDetail';
import { defaultFilters, OnFilterModelChange, OnSortModelChange } from './vehiclesTableConfig';

const style = {
	background: 'none',
	border: 0,
	color: 'blue',
	fontSize: 'inherit',
	cursor: 'pointer',
};

const numericOperators = getGridNumericOperators();

interface AccessDevicesTableProps {
	tabNav?: JSX.Element;
	setShowSnackbar: SetShowSnackbar;
	setShowDialog: SetShowDialog;
	theme: any;
	csvExportButton: ((props: any) => ReactElement) | null;
	tenantSubscriptionId?: string;
	facilityID?: string;
	tenantAccountId?: number;
}

export const VehiclesTable = ({
	facilityID,
	theme,
	tenantAccountId,
	setShowDialog,
	setShowSnackbar,
	csvExportButton,
	tabNav,
	tenantSubscriptionId,
}: AccessDevicesTableProps) => {
	const defaultOrder = {
		field: 'createdAt',
		sort: 'desc',
	};

	const dateOperators = getGridDateOperators();

	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [rowCount, setRowCount] = useState(0);
	const [sortOrder, setSortOrder] = useState(defaultOrder);
	const [filters, setFilters] = useState(defaultFilters);
	const debouncedSetFilters = useDebounce(setFilters, 500);

	const queryFilter = useMemo(
		() => [...new Map(filters.map((filter) => [filter.field, filter])).values()],
		[filters],
	);

	const facilityFilter = useMemo(
		() => (facilityID ? { propertyName: 'facilityId', op: 'equals', value: facilityID } : null),
		[facilityID],
	);

	const accountFilter = useMemo(
		() =>
			tenantAccountId ? { propertyName: 'subscriptionAccountId', op: 'equals', value: tenantAccountId } : null,
		[tenantAccountId],
	);

	const permFilters = useMemo(
		() => [...queryFilter, ...(facilityFilter ? [facilityFilter] : []), ...(accountFilter ? [accountFilter] : [])],
		[queryFilter, facilityFilter, accountFilter],
	);

	const {
		loading,
		data: vehiclesData,
		refetch,
	} = useQuery(LIST_VEHICLES_V2, {
		variables: {
			tenantSubscriptionId,
			limit: pageSize,
			offset: page * pageSize,
			order: sortOrder,
			filters: [...permFilters, ...filters],
		},
		context: {
			apiVersion: 'v2',
		},
		fetchPolicy: 'network-only', // Bypass Apollo's automated caching
	});

	const { loading: vehiclesCountLoading, data: vehiclesCount } = useQuery(LIST_VEHICLES_COUNT, {
		variables: {
			filters,
			tenantSubscriptionId,
		},
		context: {
			apiVersion: 'v2',
		},
		fetchPolicy: 'network-only',
	});

	useEffect(() => {
		if (!vehiclesCountLoading && vehiclesCount) {
			setRowCount(vehiclesCount?.listVehiclesCount?.total);
		}
	}, [vehiclesCountLoading, vehiclesCount]);

	useEffect(() => {
		setRowCount((prevRowCountState) => (rowCount !== undefined ? rowCount : prevRowCountState));
	}, [rowCount]);

	const vehicleRows = useMemo(() => vehiclesData?.listVehicles.vehicles ?? [], [vehiclesData?.listVehicles.vehicles]);

	const onSortModelChange = useCallback(
		(sortModel) => {
			OnSortModelChange({ setPaginationModel, setSortOrder })(sortModel);
		},
		[setPaginationModel, setSortOrder],
	);

	const onFilterModelChange = useCallback(
		(filterModel) => {
			OnFilterModelChange({ setFilters: debouncedSetFilters, setPaginationModel })(filterModel);
		},
		[debouncedSetFilters, setPaginationModel],
	);

	const columns = [
		{
			field: 'lastName',
			headerName: 'Last Name',
			renderCell: (params) => (
				<a
					style={style}
					onClick={() =>
						setShowDialog(
							<ParkerDetailModal
								parkerId={params.row.parkerId}
								parkerFirstName={params.row.firstName}
								parkerLastName={params.row.lastName}
								parkerAccountId={params.row.subscriptionAccountId}
								parkerSubscriptionName={params.row.subscriptionName}
								facilityId={params.row.facilityId}
								parkerFacilityName={params.row.facilityName}
								parkerFacilityNumber={params.row.facilityNumber}
								subscriptionId={params.row.subscriptionId}
								setShowSnackbar={setShowSnackbar}
								tenantSubscriptionId={tenantSubscriptionId}
								handleClose={() => {
									refetch();
									setShowDialog(null);
								}}
								theme={theme}
							/>,
						)
					}
				>
					{params.row.lastName || 'Change Me'}
				</a>
			),
		},
		{
			field: 'firstName',
			headerName: 'First Name',
			renderCell: (params) => (
				<a
					style={style}
					onClick={() =>
						setShowDialog(
							<ParkerDetailModal
								parkerId={params.row.parkerId}
								parkerFirstName={params.row.firstName}
								parkerLastName={params.row.lastName}
								parkerAccountId={params.row.subscriptionAccountId}
								parkerSubscriptionName={params.row.subscriptionName}
								facilityId={params.row.facilityId}
								parkerFacilityName={params.row.facilityName}
								parkerFacilityNumber={params.row.facilityNumber}
								subscriptionId={params.row.subscriptionId}
								setShowSnackbar={setShowSnackbar}
								tenantSubscriptionId={tenantSubscriptionId}
								handleClose={() => {
									refetch();
									setShowDialog(null);
								}}
								theme={theme}
							/>,
						)
					}
				>
					{params.row.firstName || 'Change Me'}
				</a>
			),
		},
		{
			field: 'suspensionStatus',
			headerName: 'Suspension Status',
			type: 'singleSelect',
			width: 200,
			valueOptions: [
				{ value: 'Active', label: 'Active' },
				{ value: 'Pending', label: 'Pending' },
				{ value: 'Suspension_Pending', label: 'Suspension Pending' },
				{ value: 'Suspended', label: 'Suspended' },
				{ value: 'Terminated', label: 'Terminated' },
			],
		},
		{
			field: 'terminationStatus',
			headerName: 'Termination Status',
			type: 'singleSelect',
			width: 200,
			valueOptions: [
				{ value: 'Active', label: 'Active' },
				{ value: 'Pending', label: 'Pending' },
				{ value: 'Termination_Pending', label: 'Termination Pending' },
				{ value: 'Terminated', label: 'Terminated' },
			],
		},
		{
			field: 'facilityNumber',
			headerName: 'Facility #',
			filterOperators: numericOperators,
		},
		{
			field: 'facilityName',
			headerName: 'Facility Name',
		},
		{
			field: 'subscriptionName',
			headerName: 'Subscription Name',
		},
		{
			field: 'subscriptionAccountId',
			headerName: 'Account ID',
			filterOperators: numericOperators,
			renderCell: (params) => `AC-${params.row.subscriptionAccountId}`,
		},
		{
			field: 'reservedSpace',
			headerName: 'Reserved Space',
		},
		{
			field: 'startDate',
			headerName: 'Start Date',
			filterOperators: dateOperators,
			renderCell: (params) =>
				params.row.startDate ? dayjs.utc(params.row.startDate).format('MM/DD/YYYY') : null,
		},
		{
			field: 'endDate',
			headerName: 'End Date',
			filterOperators: dateOperators,
			renderCell: (params) => (params.row.endDate ? dayjs.utc(params.row.endDate).format('MM/DD/YYYY') : null),
		},
		{
			field: 'make',
			headerName: 'Vehicle Make',
		},
		{
			field: 'year',
			headerName: 'Vehicle Year',
			filterOperators: numericOperators,
		},
		{
			field: 'model',
			headerName: 'Vehicle Model',
		},
		{
			field: 'license',
			headerName: 'License Plate',
		},
		{
			field: 'state',
			headerName: 'License State',
		},
		{
			field: 'color',
			headerName: 'Vehicle Color',
		},
		{
			field: 'decal',
			headerName: 'Vehicle Decal',
		},
	];

	const CustomToolbar = useCallback(
		() => (
			<GridToolbarContainer>
				<GridToolbarFilterButton onResize={undefined} onResizeCapture={undefined} />
				<GridToolbarColumnsButton onResize={undefined} onResizeCapture={undefined} />
				<GridToolbarDensitySelector onResize={undefined} onResizeCapture={undefined} />
				{csvExportButton ? (
					<CsvExportButtonV2
						query={GET_MONTHLY_PARKER_VEHICLES_CSV}
						queryName='getMonthlyParkerVehiclesCsv'
						args={{
							variables: {
								tenantSubscriptionId,
								order: sortOrder,
								filters: [...permFilters, ...filters],
							},
							context: {
								apiVersion: 'v2',
							},
						}}
						setShowSnackbar={setShowSnackbar}
					/>
				) : null}
			</GridToolbarContainer>
		),
		[tenantSubscriptionId, sortOrder, csvExportButton, permFilters, filters],
	);

	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			{tabNav}
			<div className='pca__datagrid-sticky--headfoot' data-cy='vehicles-datagrid-container'>
				<DataGridPro
					sx={{ height: '100%' }}
					paginationModel={{ page, pageSize }}
					onSortModelChange={onSortModelChange}
					filterMode='server'
					onFilterModelChange={onFilterModelChange}
					hideFooter={false}
					hideFooterPagination={false}
					onPaginationModelChange={setPaginationModel}
					pagination
					rows={vehicleRows || []}
					rowCount={rowCount}
					pageSizeOptions={[pageSize]}
					columns={columns}
					paginationMode='server'
					sortingMode='server'
					loading={loading || vehiclesCountLoading}
					slots={{
						toolbar: CustomToolbar,
						loadingOverlay: DatagridLinearProgress,
					}}
					initialState={{
						filter: {
							filterModel: {
								items: [
									{
										field: 'suspensionStatus',
										operator: 'isAnyOf',
										value: ['Active', 'Suspension_Pending'],
										id: 1,
									},
									{
										field: 'terminationStatus',
										operator: 'isAnyOf',
										value: ['Active', 'Termination_Pending'],
										id: 2,
									},
								],
							},
						},
					}}
					slotProps={{
						toolbar: {
							csvOptions: { disableToolbarButton: true },
							printOptions: { disableToolbarButton: true },
						},
						filterPanel: { ...datagridFilterPanelAttributes },
					}}
				/>
			</div>
		</div>
	);
};
